import { useEffect, useRef } from 'react';
import axios from 'axios';

// Access tokens are 15 min (Phase A cutover, edb9ef6). This hook keeps
// the sub-app tab logged in past that ceiling by:
//   1. Firing POST /api/auth/refresh every 12 min while the tab is open —
//      the HttpOnly `harmony_refresh` cookie set at login rides along
//      (withCredentials) and the backend hands back a fresh access token.
//   2. Firing one immediate refresh at mount if the URL-launched token is
//      already within 5 min of expiry (bookmark-reopen fast-path).
//   3. Recovering from 401s on THIS sub-app's API calls by refreshing
//      once and replaying the failed request.
//   4. Overriding Authorization on outgoing requests to THIS sub-app's
//      API from the latest tokenRef so stale closure copies of a
//      `headers` object don't pin requests to a token we've since
//      rotated past.
// On terminal refresh failure (401 from /auth/refresh itself, or a
// second 401 on the retry) the user is redirected to the shell login.
//
// This mirrors the pattern TimesheetContext already runs inline — the
// hook exists so every other sub-app (Recruitment, Expense, Asset,
// FormBuilder, Payroll, Projects, PhishPass) can share it without
// re-implementing it.

const REFRESH_INTERVAL_MS = 12 * 60 * 1000; // 12 min
const NEAR_EXPIRY_MS = 5 * 60 * 1000;       // < 5 min → refresh at mount
const REDIRECT_DELAY_MS = 500;              // brief pause so React can flush

const decodeJwt = (jwt) => {
  try {
    const [, payload] = (jwt || '').split('.');
    if (!payload) return null;
    const base = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base + '='.repeat((4 - base.length % 4) % 4)));
  } catch (_) { return null; }
};

export const useAuthRefresh = (initialToken, { backendUrl, apiPrefix }) => {
  // Live token — every caller reads via the ref so silent refresh can
  // swap it without re-creating callbacks or headers objects.
  const tokenRef = useRef(initialToken);

  // If the parent re-renders with a NEWER token (unlikely but harmless),
  // adopt it. If the incoming one is staler than what we've refreshed to,
  // ignore it — don't downgrade.
  useEffect(() => {
    if (!initialToken) return;
    try {
      const incoming = decodeJwt(initialToken);
      const current = decodeJwt(tokenRef.current);
      if ((incoming?.exp || 0) > (current?.exp || 0)) {
        tokenRef.current = initialToken;
      }
    } catch (_) {
      tokenRef.current = initialToken;
    }
  }, [initialToken]);

  useEffect(() => {
    if (!tokenRef.current) return;
    const timers = [];
    let cancelled = false;
    let redirecting = false;

    const goToShellLogin = () => {
      if (redirecting) return;
      redirecting = true;
      const loginUrl = `${window.location.origin}/login`;
      setTimeout(() => {
        if (!cancelled) { try { window.location.href = loginUrl; } catch (_) {} }
      }, REDIRECT_DELAY_MS);
    };

    // Scope interceptors to this sub-app's API + the auth-refresh endpoint,
    // so we don't touch anything foreign that might be sharing this axios.
    const isOurCall = (url) => {
      if (!url) return false;
      if (url.includes(apiPrefix)) return true;
      if (url === `${backendUrl}/api/auth/refresh`) return true;
      return false;
    };

    const reqId = axios.interceptors.request.use((config) => {
      const url = config.url || '';
      if (isOurCall(url)) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${tokenRef.current}`;
      }
      return config;
    });

    const doRefresh = async () => {
      try {
        const r = await axios.post(
          `${backendUrl}/api/auth/refresh`,
          {},
          { withCredentials: true },
        );
        if (r.data?.token && !cancelled) tokenRef.current = r.data.token;
      } catch (e) {
        if (e?.response?.status === 401) goToShellLogin();
        // Else: transient (network flap, 5xx). Next tick will retry.
      }
    };
    timers.push(setInterval(doRefresh, REFRESH_INTERVAL_MS));

    // Mount-time fast-path: only refresh if the URL-launched token is
    // already near expiry. A fresh launch skips this (the token has ~15
    // min of life left) and saves a round-trip.
    const claims = decodeJwt(tokenRef.current);
    const expMs = claims?.exp ? claims.exp * 1000 : 0;
    if (!expMs || expMs - Date.now() < NEAR_EXPIRY_MS) doRefresh();

    // One-shot 401 recovery on any call to this sub-app's API. Refresh
    // the token and replay. If the replay ALSO 401s (marked via
    // _recoveryAttempted on the config), give up and redirect.
    const respId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const url = error?.config?.url || '';
        const cfg = error?.config || {};
        const isRecoverable401 =
          error?.response?.status === 401 &&
          isOurCall(url) &&
          url !== `${backendUrl}/api/auth/refresh`;
        if (!isRecoverable401 || cfg._recoveryAttempted) {
          if (isRecoverable401) goToShellLogin();
          return Promise.reject(error);
        }
        try {
          const r = await axios.post(
            `${backendUrl}/api/auth/refresh`,
            {},
            { withCredentials: true },
          );
          if (r.data?.token) {
            tokenRef.current = r.data.token;
            cfg._recoveryAttempted = true;
            return axios(cfg);
          }
          goToShellLogin();
        } catch (_) {
          goToShellLogin();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      cancelled = true;
      timers.forEach((t) => { try { clearInterval(t); clearTimeout(t); } catch (_) {} });
      axios.interceptors.request.eject(reqId);
      axios.interceptors.response.eject(respId);
    };
  }, [backendUrl, apiPrefix]);

  return { tokenRef };
};
